import {
    CALL,
    FIRST,
    FIRST_OR_NIL,
    IF,
    IS_NOT,
    NO,
    UIButton,
    UIColor,
    UIObject,
    UIRectangle,
    UITableView,
    UITextView,
    UIView,
    wrapInNil,
    YES
} from "uicore-ts"
import { LanguageService } from "../../LanguageService"
import { CellView } from "../CellView"
import { RHTextSanitizer } from "../RH/Utils_slop/RHTextSanitizer"
import { RowView } from "../RowView"
import { CBColor } from "./CBColor"
import { CBDataViewCellDescriptor } from "./CBDataView"


/** What the data view writes onto each data object it shows, to drive tree expansion and row presentation. */
export interface CBDataViewRowState<DataType extends object = object> {
    _CB_DataView_IsExpandable?: boolean
    _CB_DataView_IsExpanded?: boolean
    _CB_DataView_ManualExpansion?: "open" | "closed"
    _CB_DataView_NestingDepth?: number
    _CB_DataView_updateRowWithNestingDepth?: (nestingDepth: number, rowView: CBTableRowView<DataType>) => void
}

export type CBDataViewRow<DataType extends object> = DataType & CBDataViewRowState<DataType>

/** A data object as the data view treats it: any of them may carry the bookkeeping fields. */
export function rowStateOf<DataType extends object>(dataItem: DataType): CBDataViewRow<DataType> {
    return dataItem as CBDataViewRow<DataType>
}

/** The column sizes the data view assigns to its table view, which rows read from their superview. */
export interface CBTableViewColumnSizes {
    cellWidths?: number[]
    cellMinimumWidths?: number[]
}

export function columnSizesOfTable(tableView: UIView | undefined): CBTableViewColumnSizes {
    const columnSizes: CBTableViewColumnSizes = {}
    if (tableView && "cellWidths" in tableView && Array.isArray(tableView.cellWidths)) {
        columnSizes.cellWidths = tableView.cellWidths
    }
    if (tableView && "cellMinimumWidths" in tableView && Array.isArray(tableView.cellMinimumWidths)) {
        columnSizes.cellMinimumWidths = tableView.cellMinimumWidths
    }
    return columnSizes
}


export class CBTableRowView<DataType extends object = object> extends RowView {
    
    readonly backgroundView: UIView
    protected readonly _bottomLineView: UIView
    dataCells: CellView[] = []
    private _data: CBDataViewRow<DataType> | undefined
    private _descriptors: CBDataViewCellDescriptor<DataType>[] = []
    
    sideCellsWidth = this.core.paddingLength
    
    _CB_DataView_IsExpanded_clickHandler?: ((sender: UIView, event: Event) => void) & { remove(): void }
    expandabilityButtonView?: UITextView
    
    highlightingBorderView?: UIView
    
    _keyboardFocusedCellIndex: number | undefined = undefined
    intrinsicHeightDidChange?: (row: CBTableRowView<DataType>, expectedHeight: number) => void
    private _lastIntrinsicHeightInvalidationKey?: string
    
    constructor(elementID: string) {
        
        super(elementID)
        
        this._bottomLineView = new UIView(elementID + "BottomLineView")
        this._bottomLineView.backgroundColor = CBColor.primaryContentColor.colorWithAlpha(0.2)
        this.addSubview(this._bottomLineView)
        
        this.backgroundView = new UIView(elementID + "BackgroundView")
        this.addSubview(this.backgroundView)
        
        this.viewHTMLElement.classList.add("InquiryRowView")
        this.backgroundView.viewHTMLElement.classList.add("InquiryRowViewBackground")
        
        this.viewHTMLElement.setAttribute("role", "row")
        
        UIObject.configureWithObject(this.contentInsets, { left: this.sideCellsWidth, right: this.sideCellsWidth })
        
    }
    
    
    get highlighted(): boolean {
        return this.highlightingBorderView?.superview == this
    }
    
    set highlighted(highlighted: boolean) {
        
        if (highlighted) {
            
            this.highlightingBorderView = this.highlightingBorderView || new UIView().configuredWithObject({
                setBorder: CALL(5, 2),
                userInteractionEnabled: NO
            })
            
            if (this.highlightingBorderView.superview != this) {
                this.addSubview(this.highlightingBorderView)
            }
            else {
                this.highlightingBorderView.moveToTopOfSuperview()
            }
            
        }
        else {
            this.highlightingBorderView?.removeFromSuperview()
        }
        
    }
    
    
    set descriptors(descriptors) {
        this._descriptors = descriptors
        this.updateContentForCurrentDescriptors()
    }
    
    get descriptors() {
        return this._descriptors
    }
    
    /**
     * The subset of descriptors that are visible (not hidden).
     * Drives cell creation and data binding. Parallel to this.dataCells.
     */
    get visibleDescriptors(): CBDataViewCellDescriptor<DataType>[] {
        return this._descriptors.filter(descriptor => !descriptor.hidden)
    }
    
    
    private updateContentForCurrentDescriptors() {
        
        function addRightBorder(view: UIView) {
            view.style.borderRightColor = CBColor.primaryContentColor.colorWithAlpha(0.2).stringValue
            view.style.borderRightWidth = "1px"
            view.style.borderRightStyle = "solid"
        }
        
        const views: UIView[] = []
        
        this.dataCells = []
        
        this.visibleDescriptors.forEach((descriptor, index, array) => {
            
            // @ts-ignore
            const view = new CellView<DataType, ReturnType<typeof descriptor.initCustomView>>()
            
            view.variant = IF(descriptor.buttonWasPressed)(() => "button")
                .ELSE_IF(descriptor.initCustomView)(() => "custom")
                .ELSE(() => "label")
            
            let buttonViewWithDescriptorAction: UIButton | undefined
            const ensureCurrentButtonHandlesDescriptorAction = () => {
                const buttonView = view.buttonView
                if (!descriptor.buttonWasPressed || !buttonView || buttonViewWithDescriptorAction === buttonView) {
                    return
                }
                buttonView.addTargetForControlEvents(
                    [UIButton.controlEvent.EnterDown, UIButton.controlEvent.PointerUpInside],
                    (sender, event) => {
                        const buttonWasPressed = FIRST_OR_NIL(descriptor.buttonWasPressed)
                        if (this.data) {
                            buttonWasPressed(view, this.data, event as MouseEvent | TouchEvent | KeyboardEvent)
                        }
                    }
                )
                buttonViewWithDescriptorAction = buttonView
            }
            
            view.updateCellContent = (dataItem: DataType) => {
                descriptor.updateCell?.(view, dataItem)
                ensureCurrentButtonHandlesDescriptorAction()
            }
            
            if (descriptor.cellLabelAlignment && view.variant == "label") {
                view.titleLabel!.textAlignment = descriptor.cellLabelAlignment
            }
            
            if (!view.customView && descriptor.initCustomView) {
                view.customView = descriptor.initCustomView()
            }
            
            view.viewHTMLElement.setAttribute("role", "gridcell")
            view.viewHTMLElement.setAttribute("aria-colindex", String(index + 1))
            
            let initCell = descriptor.initCell
            if (!initCell) {
                initCell = (cellView: CellView) => {
                    if (cellView.variant == "label") {
                        cellView.titleLabel!.text = LanguageService.stringForCurrentLanguage(FIRST(
                            descriptor.defaultTitle,
                            descriptor.title
                        ))
                    }
                    else if (cellView.variant == "button") {
                        cellView.buttonView!.titleLabel.text = LanguageService.stringForCurrentLanguage(FIRST(
                            descriptor.defaultTitle,
                            descriptor.title
                        ))
                    }
                }
            }
            initCell(view)
            ensureCurrentButtonHandlesDescriptorAction()
            
            if (index < array.length - 1) {
                addRightBorder(view)
            }
            
            views.push(view)
            this.dataCells.push(view)
            
        })
        
        this.cells = views
        
    }
    
    
    override get cellWeights(): number[] {
        return (this.superview as UITableView)?.cellWeights ?? super.cellWeights ?? [1].arrayByRepeating(
            this.cells.length)
    }
    
    override set cellWeights(widths: number[]) {
        super.cellWeights = widths
    }

    override get cellWidths(): number[] {
        return columnSizesOfTable(this.superview).cellWidths ?? super.cellWidths
    }

    override set cellWidths(widths: number[]) {
        super.cellWidths = widths
    }

    override get cellMinimumWidths(): number[] {
        return columnSizesOfTable(this.superview).cellMinimumWidths ?? super.cellMinimumWidths
    }

    override set cellMinimumWidths(widths: number[]) {
        super.cellMinimumWidths = widths
    }
    
    override get frame(): UIRectangle & { zIndex?: number } {
        return super.frame
    }
    
    override set frame(frame: UIRectangle & { zIndex?: number }) {
        
        super.frame = frame
        
    }
    
    
    set data(data: CBDataViewRow<DataType> | undefined) {
        this._lastIntrinsicHeightInvalidationKey = undefined
        this._data = data
        this.updateContentForCurrentData(data)
    }
    
    get data() {
        return this._data
    }


    _updateDisclosureControlPresentation() {
        if (!this.expandabilityButtonView) {
            return
        }
        const disclosureText = IF(this.data?._CB_DataView_IsExpanded)(() =>
            "&#x25BC;"
        ).ELSE(() =>
            "&#x25B6;"
        )
        if (this.expandabilityButtonView.text != disclosureText) {
            this.expandabilityButtonView.text = disclosureText
        }
    }
    
    
    private updateContentForCurrentData(data: CBDataViewRow<DataType> | undefined) {
        
        if (IS_NOT(data)) {
            return
        }

        data._CB_DataView_updateRowWithNestingDepth?.(data._CB_DataView_NestingDepth ?? 0, this)
        
        this.visibleDescriptors.forEach((descriptor, index) => {
            
            const cellView = FIRST_OR_NIL(this.dataCells[index])
            
            if (data._CB_DataView_IsExpandable && index == 0) {
                
                this.expandabilityButtonView = this.expandabilityButtonView ?? new UITextView(
                    undefined,
                    UITextView.type.span
                ).configuredWithObject({ style: { userSelect: "none", cursor: "pointer" } })
                
                this._updateDisclosureControlPresentation()
                
                const clickHandler = (sender: UIView, event: Event) => {
                    data._CB_DataView_IsExpanded = !data._CB_DataView_IsExpanded
                    this.updateContentForCurrentData(data)
                    this.expandedValueDidChange(sender as CBTableRowView, event as MouseEvent)
                }
                clickHandler.remove = () => {
                    this.expandabilityButtonView?.removeTargetForControlEvents(
                        [UIView.controlEvent.PointerUpInside, UIView.controlEvent.EnterDown],
                        clickHandler
                    )
                }
                
                this._CB_DataView_IsExpanded_clickHandler?.remove()
                this._CB_DataView_IsExpanded_clickHandler = clickHandler
                // @ts-ignore
                // noinspection JSConstantReassignment
                this.expandabilityButtonView.controlEventTargetAccumulator.EnterDown.PointerUpInside = clickHandler
                
                if (this.expandabilityButtonView.superview != this) {
                    this.addSubview(this.expandabilityButtonView)
                }
                this.expandabilityButtonView.moveToTopOfSuperview()
                
            }
            else if (index == 0) {
                
                this._CB_DataView_IsExpanded_clickHandler?.remove()
                this.expandabilityButtonView?.removeFromSuperview()
                
            }
            
            if (cellView.variant == "label") {
                const rawCellText = FIRST(
                    IF(descriptor.keyPath)(() => UIObject.valueForKeyPath(descriptor.keyPath!, data))(),
                    descriptor.defaultTitle
                )
                let plainCellText = ""
                if (rawCellText != undefined) {
                    plainCellText = "" + rawCellText
                }
                // A key-path binding is data, not trusted markup. Encode it before
                // UITextView's HTML-backed text setter runs; custom updateCell
                // rendering may replace it immediately, but the default binding
                // must never create a transient executable DOM node first.
                cellView.titleLabel!.text = RHTextSanitizer.escapedHTML(plainCellText)
                cellView.titleLabel!.changesOften = YES
                cellView.hoverText = cellView.titleLabel!.text || undefined
            }
            
            cellView.updateCellContent(data)
            
        })
        
        // this.clearIntrinsicSizeCache()
        // this.setNeedsLayout()
        
    }
    
    
    expandedValueDidChange(sender: CBTableRowView, event: MouseEvent) {
        // Overridden by CBDataView
    }
    
    
    firstButtonCellIndex(): number {
        const index = this.dataCells.findIndex(cell => cell.isAButton)
        return index === -1 ? 0 : index
    }
    
    
    setKeyboardFocusedCellIndex(index: number | undefined) {
        
        this._keyboardFocusedCellIndex = index
        
        this.backgroundView.backgroundColor = index !== undefined
                                              ? new UIColor("rgba(0, 100, 255, 0.07)")
                                              : UIColor.clearColor
        
        this.dataCells.forEach((cell, cellIndex) => {
            if (cell.variant == "button") {
                cell.buttonView!.highlighted = index !== undefined && cellIndex === index
            }
        })
        
    }
    
    
    override boundsDidChange(bounds: UIRectangle) {
        
        super.boundsDidChange(bounds)
        this._lastIntrinsicHeightInvalidationKey = undefined
        
        const tableView = wrapInNil(this.superview as UITableView)
        tableView.invalidateSizeOfRowWithIndex(this._UITableViewRowIndex ?? 0)
        
    }

    override didLayoutSubviews() {
        super.didLayoutSubviews()
        this._reportIntrinsicHeightIfNeeded()
    }

    private _reportIntrinsicHeightIfNeeded() {
        const tableView = this.superview as UITableView | undefined
        const rowIndex = this._UITableViewRowIndex
        if (!tableView || rowIndex === undefined || tableView.allRowsHaveEqualHeight || !this.isMemberOfViewTree) {
            return
        }

        const width = this.frame.width
        if (width <= 0) {
            return
        }

        const expectedHeight = this.intrinsicContentHeight(width)
        const currentHeight = this.frame.height
        if (Math.abs(expectedHeight - currentHeight) < 0.5) {
            this._lastIntrinsicHeightInvalidationKey = undefined
            return
        }

        const invalidationKey = [
            rowIndex,
            width.integerValue,
            expectedHeight.integerValue,
            currentHeight.integerValue
        ].join(":")
        if (invalidationKey === this._lastIntrinsicHeightInvalidationKey) {
            return
        }

        this._lastIntrinsicHeightInvalidationKey = invalidationKey
        if (this.intrinsicHeightDidChange) {
            this.intrinsicHeightDidChange(this, expectedHeight)
            return
        }

        tableView.invalidateSizeOfRowWithIndex(rowIndex)
        tableView.setNeedsLayout()
    }
    
    
    override layoutSubviews() {
        
        const nestingDepth = this.data?._CB_DataView_NestingDepth ?? 0
        const expandabilityLeftOffset = 15
        const buttonWidth = 15
        const padding = this.core.paddingLength
        
        // Update firstCellLeftInset BEFORE calling super.layoutSubviews()
        if (this.expandabilityButtonView?.superview === this) {
            // Added buttonWidth and reduced gap (now + 2 instead of + 5)
            this.firstCellLeftInset = (nestingDepth * expandabilityLeftOffset) + buttonWidth
        }
        else if (nestingDepth > 0) {
            this.firstCellLeftInset = (nestingDepth * expandabilityLeftOffset) + buttonWidth
        }
        else {
            this.firstCellLeftInset = 0
        }
        
        super.layoutSubviews()
        
        this.backgroundView.weakFrame = this.bounds.rectangleWithInsets(padding, padding, 0, 0)
        this._bottomLineView.weakFrame = this.bounds.rectangleWithHeight(1, 1)
            .rectangleWithInsets(padding, padding, 0, 0)
        
        if (this.highlightingBorderView) {
            this.highlightingBorderView.weakFrame = this.bounds
        }
        
        if (this.expandabilityButtonView?.superview === this) {
            this.expandabilityButtonView.frame = this.bounds
                .rectangleWithWidth(buttonWidth)
                .rectangleWithHeight(this.expandabilityButtonView, 0.5)
                .rectangleByAddingX(this.contentInsets.left + padding + (nestingDepth * expandabilityLeftOffset))
        }
        
    }
    
    
}
